import { resolveViewerId } from 'Pods/Shared/Hooks/useViewerId'

import { DEFAULT_GENERATION_STATUS, MAX_GENERATION_RESULTS } from './constants'
import { generationStatusStore } from './index'

// Fields the global status store mirrors from the /ai studio's generation slice.
// To expose a new live-status field globally, add it here - this is the single
// place the mirror reads, so writers never have to remember a separate call.
const MIRRORED_KEYS = [
  'isGenerating',
  'isEnhancing',
  'enhancingSessionId',
  'runId',
  'sessionId',
  'generationStartedAt',
  'activeRunCount',
]

/**
 * Mirrors a generation-slice patch into the global status store. Called from the
 * studio store's single setGenerationState choke point, so every live-status change
 * is reflected without per-feature wiring.
 * @param {object} patch - The partial generation-slice update being applied.
 */
export const publishGenerationStatus = (patch) => {
  const mirrored = {}
  MIRRORED_KEYS.forEach((key) => {
    if (key in patch) {
      mirrored[key] = patch[key]
    }
  })

  // The studio patches carry the full in-flight run list; the mirror only needs
  // how many there are (the nav spinner count).
  if ('activeRuns' in patch && Array.isArray(patch.activeRuns)) {
    mirrored.activeRunCount = patch.activeRuns.length
  }

  // A run starting stamps the owner so the global nav only reveals live status to the
  // user who started it. Only stamp when identity has resolved: this same action runs
  // from the off-/ai watcher (useGenerationStatusWatcher) on tabs where the viewer id
  // may not be ready yet, and writing null there would clobber the real owner across
  // every tab via the storage sync. Finished results survive further runs starting
  // (the dropdown stacks them), EXCEPT when the owner changes: localStorage is shared
  // by every user of a browser, so another account starting a run must not inherit
  // the previous owner's results.
  if (patch.isGenerating === true) {
    const viewerId = resolveViewerId()
    if (viewerId != null) {
      const previousOwner = generationStatusStore.getState().userId
      if (previousOwner != null && String(previousOwner) !== String(viewerId)) {
        mirrored.lastResult = null
        mirrored.lastResults = []
      }
      mirrored.userId = viewerId
    }
  }

  if (Object.keys(mirrored).length === 0) {
    return
  }

  generationStatusStore.setState(mirrored)
}

/**
 * Records the outcome of a finished run: it becomes the newest entry of the unviewed
 * result list (deduped by runId, capped) and the lastResult scalar consumers key on.
 * @param {object} lastResult - The generated/failed run info (see DEFAULT shape).
 */
export const setGenerationResult = (lastResult) => {
  const viewerId = resolveViewerId()
  const existing = generationStatusStore.getState().lastResults || []
  const lastResults = [
    lastResult,
    ...existing.filter((result) => String(result?.runId) !== String(lastResult?.runId)),
  ].slice(0, MAX_GENERATION_RESULTS)

  // Re-stamp the owner on completion, but never blank a resolved owner with null (the
  // watcher can settle a run on a tab whose viewer id has not loaded yet).
  generationStatusStore.setState(
    viewerId != null ? { lastResult, lastResults, userId: viewerId } : { lastResult, lastResults }
  )
}

// Clears every finished-run outcome (e.g. the mobile aggregate action dismissing the
// "model ready" state) without touching live status.
export const clearGenerationResult = () => {
  generationStatusStore.setState({ lastResult: null, lastResults: [] })
}

/**
 * Clears one finished run's outcome (e.g. opening its dropdown row or toast) while
 * the other stacked results stay. lastResult re-points at the newest remaining run.
 * @param {number|string} runId - The opened/dismissed run
 */
export const clearGenerationResultForRun = (runId) => {
  if (runId == null) {
    clearGenerationResult()
    return
  }

  const existing = generationStatusStore.getState().lastResults || []
  const lastResults = existing.filter((result) => String(result?.runId) !== String(runId))
  generationStatusStore.setState({ lastResults, lastResult: lastResults[0] ?? null })
}

// Records that the user explicitly closed the "model ready" toast for this run, so it
// never re-shows across reloads/tabs. Keeps lastResult intact (other consumers, e.g. the
// Create-with-AI dropdown, still read it) - only the toast's own visibility is suppressed.
export const dismissGenerationResult = (runId) => {
  if (runId == null) {
    return
  }

  generationStatusStore.setState({ dismissedRunId: runId })
}

export const resetGenerationStatus = () => {
  generationStatusStore.setState({ ...DEFAULT_GENERATION_STATUS })
}
